import React, { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { dashboardAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Briefcase, 
  Users, 
  UserCog, 
  DollarSign, 
  TrendingUp, 
  TrendingDown,
  Calendar,
  ArrowUpRight
} from 'lucide-react';
import { cn } from '../lib/utils';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend 
} from 'recharts';

const DashboardPage = () => {
  const { t, direction, language } = useLanguage();
  const { isAdmin, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentCases, setRecentCases] = useState([]);
  const [monthlyFinance, setMonthlyFinance] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [statsRes, casesRes, financeRes] = await Promise.all([
          dashboardAPI.getStats(),
          dashboardAPI.getRecentCases(),
          dashboardAPI.getMonthlyFinance()
        ]);
        setStats(statsRes.data);
        setRecentCases(casesRes.data);
        setMonthlyFinance(financeRes.data);
      } catch (error) {
        console.error('Dashboard fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(language === 'ar' ? 'ar-IQ' : 'en-US', {
      style: 'currency',
      currency: 'IQD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      active: { class: 'status-active', label: t('active') },
      pending: { class: 'status-pending', label: t('pending') },
      closed: { class: 'status-closed', label: t('closed') },
      archived: { class: 'status-archived', label: t('archived') }
    };
    const s = statusMap[status] || statusMap.active;
    return <Badge className={s.class}>{s.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page">
      <Header title={t('dashboard')} />
      
      <div className="p-6 space-y-6">
        {/* Welcome Message for Lawyers */}
        {!isAdmin && user?.role === 'lawyer' && (
          <Card className="bg-[hsl(var(--accent))]/10 border-[hsl(var(--accent))]/30">
            <CardContent className="pt-6">
              <h2 className="text-xl font-bold">
                {language === 'ar' ? `مرحباً، ${user.full_name}` : `Welcome, ${user.full_name}`}
              </h2>
              <p className="text-muted-foreground mt-1">
                {language === 'ar' ? 'هذه لوحة التحكم الخاصة بك - تعرض قضاياك فقط' : 'This is your personal dashboard - showing only your cases'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* KPI Cards */}
        <div className={cn(
          "grid gap-4",
          isAdmin ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-4" : "grid-cols-1 md:grid-cols-2"
        )}>
          {/* Cases Card - Always visible */}
          <Card className="stat-card fade-in" data-testid="stat-cases">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    {isAdmin ? t('totalCases') : (language === 'ar' ? 'قضاياي' : 'My Cases')}
                  </p>
                  <h3 className="text-3xl font-bold mt-1">{stats?.cases?.total || 0}</h3>
                  <div className="flex items-center gap-2 mt-2 text-sm">
                    <span className="text-emerald-600 dark:text-emerald-400">
                      {stats?.cases?.active || 0} {t('active')}
                    </span>
                    <span className="text-muted-foreground">•</span>
                    <span className="text-amber-600 dark:text-amber-400">
                      {stats?.cases?.pending || 0} {t('pending')}
                    </span>
                  </div>
                </div>
                <div className="p-3 rounded-sm bg-primary/10">
                  <Briefcase className="w-6 h-6 text-primary" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Clients Card - Admin Only */}
          {isAdmin && (
            <Card className="stat-card fade-in" style={{ animationDelay: '0.1s' }} data-testid="stat-clients">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('totalClients')}</p>
                    <h3 className="text-3xl font-bold mt-1">{stats?.clients || 0}</h3>
                  </div>
                  <div className="p-3 rounded-sm bg-blue-500/10">
                    <Users className="w-6 h-6 text-blue-500" strokeWidth={1.5} />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Revenue Card */}
          <Card className="stat-card fade-in" style={{ animationDelay: '0.2s' }} data-testid="stat-income">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    {isAdmin ? t('totalIncome') : (language === 'ar' ? 'إيراداتي' : 'My Revenue')}
                  </p>
                  <h3 className="text-3xl font-bold mt-1 text-emerald-600 dark:text-emerald-400">
                    {formatCurrency(stats?.finance?.total_income || 0)}
                  </h3>
                  <div className="flex items-center gap-1 mt-2 text-sm text-emerald-600">
                    <TrendingUp className="w-4 h-4" />
                    <span>{t('income')}</span>
                  </div>
                </div>
                <div className="p-3 rounded-sm bg-emerald-500/10">
                  <DollarSign className="w-6 h-6 text-emerald-500" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Profit Card - Admin Only */}
          {isAdmin && (
            <Card className="stat-card fade-in" style={{ animationDelay: '0.3s' }} data-testid="stat-profit">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('netProfit')}</p>
                    <h3 className={cn(
                      'text-3xl font-bold mt-1',
                      (stats?.finance?.profit || 0) >= 0 
                        ? 'text-emerald-600 dark:text-emerald-400' 
                        : 'text-red-600 dark:text-red-400'
                    )}>
                      {formatCurrency(stats?.finance?.profit || 0)}
                    </h3>
                    <div className="flex items-center gap-1 mt-2 text-sm text-muted-foreground">
                      <span>{t('totalExpenses')}: {formatCurrency(stats?.finance?.total_expenses || 0)}</span>
                    </div>
                  </div>
                  <div className="p-3 rounded-sm bg-[hsl(var(--accent))]/10">
                    {(stats?.finance?.profit || 0) >= 0 ? (
                      <TrendingUp className="w-6 h-6 text-[hsl(var(--accent))]" strokeWidth={1.5} />
                    ) : (
                      <TrendingDown className="w-6 h-6 text-red-500" strokeWidth={1.5} />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Main Content Grid */}
        <div className={cn(
          "grid gap-6",
          isAdmin ? "grid-cols-1 lg:grid-cols-3" : "grid-cols-1"
        )}>
          {/* Financial Chart - Admin Only */}
          {isAdmin && (
            <Card className="lg:col-span-2 fade-in" style={{ animationDelay: '0.4s' }} data-testid="finance-chart">
              <CardHeader>
                <CardTitle className="text-lg">{t('financialOverview')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {monthlyFinance.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={monthlyFinance}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="month" 
                          stroke="hsl(var(--muted-foreground))"
                          fontSize={12}
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          fontSize={12}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '4px'
                          }}
                        />
                        <Legend />
                        <Bar 
                          dataKey="income" 
                          name={t('income')} 
                          fill="hsl(var(--chart-2))" 
                          radius={[4, 4, 0, 0]}
                        />
                        <Bar 
                          dataKey="expense" 
                          name={t('expense')} 
                          fill="hsl(var(--destructive))" 
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                      {t('noData')}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Upcoming Events */}
          <Card className="fade-in" style={{ animationDelay: '0.5s' }} data-testid="upcoming-events">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">{t('upcomingEvents')}</CardTitle>
              <Calendar className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats?.upcoming_events?.length > 0 ? (
                  stats.upcoming_events.map((event, idx) => (
                    <div 
                      key={event.id || idx}
                      className="flex items-start gap-3 p-3 rounded-sm bg-muted/50 hover:bg-muted transition-colors"
                    >
                      <div className="w-10 h-10 rounded-sm bg-[hsl(var(--accent))]/10 flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-5 h-5 text-[hsl(var(--accent))]" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{event.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {event.date} {event.time && `• ${event.time}`}
                        </p>
                        {event.location && (
                          <p className="text-xs text-muted-foreground truncate">{event.location}</p>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    {t('noData')}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Cases */}
        <Card className="fade-in" style={{ animationDelay: '0.6s' }} data-testid="recent-cases">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">{t('recentCases')}</CardTitle>
            <a 
              href="/cases" 
              className="flex items-center gap-1 text-sm text-[hsl(var(--accent))] hover:underline"
            >
              {t('view')} {t('all')}
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </CardHeader>
          <CardContent>
            {recentCases.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t('caseNumber')}</th>
                      <th>{t('caseTitle')}</th>
                      <th>{t('caseType')}</th>
                      <th>{t('caseStatus')}</th>
                      <th>{t('createdAt')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentCases.map((caseItem) => (
                      <tr key={caseItem.id}>
                        <td className="font-mono text-sm">{caseItem.case_number}</td>
                        <td className="font-medium">{caseItem.title}</td>
                        <td>{caseItem.case_type}</td>
                        <td>{getStatusBadge(caseItem.status)}</td>
                        <td className="text-muted-foreground text-sm">
                          {new Date(caseItem.created_at).toLocaleDateString(
                            language === 'ar' ? 'ar-SA' : 'en-US'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">
                {t('noData')}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
